import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import { BranchBadge } from '../components/BranchUI';
import { LoadingScreen } from '../components/LoadingScreen';
import { FinanceDeadlineWidget, ScenarioDiffFloat } from '../AnalysesPage';
import ProjectNotes from '../components/ProjectNotes';
import CoInvestorsSection from '../components/CoInvestorsSection';
import FinalDossierTab from '../FinalDossierTab';
import ContractsSection from '../components/ContractsSection';
import { AddDelayModal } from '../components/AddDelayModal';
import WorksScheduleSection from '../components/WorksScheduleSection';
import WorksBudgetSection from '../components/WorksBudgetSection';
import ProjectAnalysisInline from '../components/ProjectAnalysisInline';
import ArtisanInvitePanel from '../components/ArtisanInvitePanel';
import MaitriseOeuvreCard from '../components/MaitriseOeuvreCard';
import ProjectChecklist from '../components/ProjectChecklist';
import ProjectAuditTrail from '../components/ProjectAuditTrail';
import CommissionInvoicesCard from '../components/CommissionInvoicesCard';
import MoeSection from '../components/MoeSection';

// Section repliable pour epurer la page projet
// Menu flottant diff scenarios au niveau page projet (analyse rechargee a chaque ouverture)
const ProjectScenarioDiff = ({ analysisId }) => {
  const [doc, setDoc] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!analysisId) return;
    axios.get(`${API}/analyses/${analysisId}`).then(r => setDoc(r.data)).catch(() => {});
  }, [analysisId, tick]);
  if (!doc) return null;
  return <ScenarioDiffFloat form={doc} onBeforeOpen={() => setTick(t => t + 1)} />;
};

const Collapsible = ({ title, subtitle, defaultOpen = false, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="realease-card overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
        data-testid={`${testId}-toggle`}>
        <div>
          <span className="font-semibold text-gray-900">{title}</span>
          {subtitle && <span className="ml-2 text-xs text-gray-400">{subtitle}</span>}
        </div>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && <div className="border-t border-gray-100">{children}</div>}
    </div>
  );
};

const ProjectDetailPage = () => {
  const [project, setProject] = useState(null);
  const [delays, setDelays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showAddDelay, setShowAddDelay] = useState(false);
  const [editDelay, setEditDelay] = useState(null);
  const [showChangeClient, setShowChangeClient] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [budgetKey, setBudgetKey] = useState(0);
  const [planningKey, setPlanningKey] = useState(0);
  const [pauseReason, setPauseReason] = useState('');
  const [showArchivedDelays, setShowArchivedDelays] = useState(false);
  const projectId = window.location.pathname.split('/').pop();
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [pr, dr] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/delays?project_id=${projectId}`)
      ]);
      setProject(pr.data); setDelays(dr.data);
      setEditForm({ name: pr.data.name, status: pr.data.status, progress: pr.data.progress, budget_planned: pr.data.budget_planned, budget_spent: pr.data.budget_spent, comment: pr.data.comment, risk_level: pr.data.risk_level, assigned_to: pr.data.assigned_to, branch: pr.data.branch || 'bretagne' });
    } catch {} finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    axios.get(`${API}/users/assignable`).then(r => setAssignableUsers(r.data || [])).catch(() => {});
  }, []);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/projects/${projectId}`, editForm);
      setEditing(false); fetchData();
    } catch (e) { console.error(e); }
  };

  const handlePauseToggle = async () => {
    try {
      const newPaused = !project?.paused;
      await axios.post(`${API}/projects/${projectId}/pause`, {
        paused: newPaused,
        reason: newPaused ? pauseReason : undefined,
      });
      setShowPauseModal(false);
      setPauseReason('');
      fetchData();
    } catch (e) { console.error(e); }
  };

  const statusLabel = { initiation: 'Initiation', acquisition: 'Acquisition', notaire: 'Notaire', administratif: 'Administratif', travaux: 'Travaux', livraison: 'Livraison', termine: 'Termine' };
  const alertIcons = { green: 'text-green-500', yellow: 'text-yellow-500', red: 'text-red-500', gray: 'text-gray-400' };

  if (loading) return <LoadingScreen />;
  if (!project) return <div className="p-8 text-center text-gray-500">Projet non trouve</div>;

  const canEdit = ['julie', 'admin', 'amelie', 'ceo', 'carla', 'collaborateur'].includes(user?.role);

  return (
    <div className="p-4 lg:p-8" data-testid="project-detail">
      {/* Historique des actions (audit) — deroulant en haut du dossier */}
      <div className="mb-4">
        <ProjectAuditTrail projectId={projectId} />
      </div>
      <a href="/projects" className="text-[#008A8D] hover:underline text-sm mb-2 inline-block">&larr; Retour aux projets</a>
      {project.paused && (
        <div className="mb-4 p-4 rounded-lg bg-orange-50 border border-orange-200 flex items-center gap-3" data-testid="project-paused-banner">
          <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m9-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <div className="flex-1">
            <p className="font-semibold text-orange-900">Projet en pause</p>
            {project.paused_reason && <p className="text-sm text-orange-700">{project.paused_reason}</p>}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
          <p className="text-gray-600 flex items-center gap-2 flex-wrap">
            <span>{project.client_name}</span>
            {canEdit && (
              <button onClick={() => setShowChangeClient(true)}
                       title="Changer le client de ce projet"
                       className="text-xs px-2 py-0.5 border border-[#008A8D] text-[#008A8D] rounded hover:bg-[#008A8D]/5"
                       data-testid="change-client-btn">
                Changer client
              </button>
            )}
            {project.geographic_zone && <span>| {project.geographic_zone}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {project.drive_folder_url && (
            <a href={project.drive_folder_url} target="_blank" rel="noreferrer"
               className="px-4 py-2 bg-white border border-[#A57F4F] text-[#A57F4F] text-sm font-semibold rounded-lg hover:bg-[#A57F4F]/5 flex items-center gap-1.5"
               data-testid="open-drive-folder-btn"
               title="Ouvrir le dossier Drive du projet">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              Dossier Drive
            </a>
          )}
          {project.analysis_id && (
            <a href={`/analyses/${project.analysis_id}`}
               className="px-4 py-2 bg-[#003E40] text-white text-sm font-semibold rounded-lg hover:bg-[#005558] flex items-center gap-1.5"
               data-testid="open-analysis-btn"
               title="Voir/modifier l'analyse de rentabilite">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              Analyse rentabilite
            </a>
          )}
          {canEdit && !editing && <button onClick={() => setEditing(true)} className="realease-btn-primary" data-testid="edit-project-btn">Modifier</button>}
          {canEdit && !editing && (
            <button
              onClick={() => { if (project?.paused) { handlePauseToggle(); } else { setShowPauseModal(true); } }}
              className={`px-4 py-2 text-sm font-semibold rounded-lg flex items-center gap-1.5 ${project?.paused ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}
              data-testid="pause-resume-project-btn"
              title={project?.paused ? 'Reprendre le projet' : 'Mettre le projet en pause'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={project?.paused ? "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" : "M10 9v6m4-6v6"} /></svg>
              {project?.paused ? 'Reprendre' : 'Pause'}
            </button>
          )}
          {editing && <div className="flex gap-2"><button onClick={handleSave} className="realease-btn-primary">Sauvegarder</button><button onClick={() => setEditing(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Annuler</button></div>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Project Info */}
          <div className="realease-card p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Informations projet</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="realease-label">Statut</label>
                {editing ? (
                  <select value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})} className="realease-input" data-testid="edit-status">
                    {Object.entries(statusLabel).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                ) : <p className="font-medium">{statusLabel[project.status]}</p>}
              </div>
              <div>
                <label className="realease-label">Avancement</label>
                {editing ? <input type="number" min="0" max="100" value={editForm.progress} onChange={e => setEditForm({...editForm, progress: parseInt(e.target.value) || 0})} className="realease-input" data-testid="edit-progress" />
                : <div><div className="flex justify-between text-sm mb-1"><span>{project.progress}%</span></div><div className="progress-bar"><div className="progress-bar-fill" style={{width:`${project.progress}%`}}></div></div></div>}
              </div>
              <div>
                <label className="realease-label">Budget prevu</label>
                <p className="font-medium">{project.budget_planned ? `${project.budget_planned.toLocaleString('fr-FR')} EUR` : 'Non defini'}</p>
              </div>
              <div>
                <label className="realease-label">Budget engage</label>
                {editing ? <input type="number" value={editForm.budget_spent} onChange={e => setEditForm({...editForm, budget_spent: parseFloat(e.target.value) || 0})} className="realease-input" />
                : <p className="font-medium">{(project.budget_spent || 0).toLocaleString('fr-FR')} EUR</p>}
              </div>
              <div>
                <label className="realease-label">Niveau de risque</label>
                {editing ? (
                  <select value={editForm.risk_level} onChange={e => setEditForm({...editForm, risk_level: e.target.value})} className="realease-input">
                    <option value="low">Faible</option><option value="medium">Moyen</option><option value="high">Eleve</option>
                  </select>
                ) : <p className={`font-medium ${project.risk_level === 'high' ? 'text-red-600' : project.risk_level === 'medium' ? 'text-yellow-600' : 'text-green-600'}`}>{project.risk_level === 'high' ? 'Eleve' : project.risk_level === 'medium' ? 'Moyen' : 'Faible'}</p>}
              </div>
              <div>
                <label className="realease-label">Assigne a</label>
                {editing ? (
                  <select value={editForm.assigned_to || ''} onChange={e => setEditForm({...editForm, assigned_to: e.target.value || null})} className="realease-input" data-testid="edit-assigned-to">
                    <option value="">- Non assigne -</option>
                    {assignableUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
                  </select>
                ) : (
                  <p className="font-medium" data-testid="project-assigned-to">
                    {(() => {
                      const u = assignableUsers.find(x => x.user_id === project.assigned_to);
                      return u ? `${u.name} (${u.role})` : (project.assigned_to ? project.assigned_to : 'Non assigne');
                    })()}
                  </p>
                )}
              </div>
              <div>
                <label className="realease-label">Branche</label>
                {editing && user?.cross_branch_access ? (
                  <select value={editForm.branch || 'bretagne'} onChange={e => setEditForm({...editForm, branch: e.target.value})} className="realease-input" data-testid="edit-project-branch">
                    <option value="bretagne">Realease Bretagne</option>
                    <option value="nord">Realease Nord</option>
                  </select>
                ) : (
                  <p className="font-medium" data-testid="project-branch">
                    {(project.branch || 'bretagne') === 'nord' ? 'Realease Nord' : 'Realease Bretagne'}
                    {editing && !user?.cross_branch_access && <span className="block text-[10px] text-gray-400">Modifiable par la Direction uniquement</span>}
                  </p>
                )}
              </div>
              {editing && (
                <div className="col-span-2">
                  <label className="realease-label">Nom du projet</label>
                  <input value={editForm.name || ''} onChange={e => setEditForm({...editForm, name: e.target.value})} className="realease-input" data-testid="edit-project-name" />
                </div>
              )}
              {editing && (
                <div>
                  <label className="realease-label">Budget prevu (EUR)</label>
                  <input type="number" value={editForm.budget_planned ?? ''} onChange={e => setEditForm({...editForm, budget_planned: e.target.value === '' ? null : parseFloat(e.target.value)})} className="realease-input" data-testid="edit-budget-planned" />
                </div>
              )}
            </div>
            {(editing || project.comment) && (
              <div className="mt-4">
                <label className="realease-label">Commentaire</label>
                {editing ? <textarea value={editForm.comment || ''} onChange={e => setEditForm({...editForm, comment: e.target.value})} className="realease-input h-20 resize-none" />
                : <p className="text-sm text-gray-600">{project.comment}</p>}
              </div>
            )}
          </div>

          {/* Delays section */}
          <div className="realease-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Delais lies au projet</h3>
              <div className="flex items-center gap-3">
                {delays.some(d => d.status === 'termine') && (
                  <button onClick={() => setShowArchivedDelays(s => !s)}
                          className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${showArchivedDelays ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          data-testid="toggle-archived-delays">
                    Archives ({delays.filter(d => d.status === 'termine').length})
                  </button>
                )}
                {['amelie', 'admin', 'julie', 'ceo', 'carla'].includes(user?.role) && (
                  <button onClick={() => setShowAddDelay(true)}
                          className="px-3 py-1.5 bg-[#008A8D] text-white text-xs rounded-lg font-medium hover:bg-[#006B6D] transition-colors flex items-center gap-1.5"
                          data-testid="add-delay-from-project-btn">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                    Ajouter un delai
                  </button>
                )}
                <a href={`/delais?project_id=${projectId}`} className="text-[#008A8D] text-sm hover:underline">Gerer les delais &rarr;</a>
              </div>
            </div>
            {(() => {
              const visible = delays.filter(d => showArchivedDelays ? d.status === 'termine' : d.status !== 'termine');
              if (visible.length === 0) {
                return <p className="text-gray-500 text-sm">{showArchivedDelays ? 'Aucun delai archive.' : 'Aucun delai en cours pour ce projet.'}</p>;
              }
              return (
              <div className="space-y-3">
                {visible.map(d => (
                  <div key={d.delay_id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50" data-testid={`delay-item-${d.delay_id}`}>
                    <div className="flex items-center gap-3">
                      <span className={`text-xl ${alertIcons[d.alert] || 'text-gray-400'}`}>
                        {d.alert === 'green' ? '\u25CF' : d.alert === 'yellow' ? '\u25CF' : d.alert === 'red' ? '\u25CF' : '\u25CB'}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{d.description}</p>
                        <p className="text-xs text-gray-500">{d.delay_type} | Cible: {d.target_date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`status-badge ${d.status === 'en_retard' ? 'bg-red-100 text-red-800' : d.status === 'termine' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{d.status}</span>
                      {canEdit && (
                        <button onClick={() => setEditDelay(d)}
                                className="text-xs text-[#008A8D] hover:underline font-medium"
                                title="Modifier ce delai"
                                data-testid={`edit-delay-project-${d.delay_id}`}>
                          Modifier
                        </button>
                      )}
                      {canEdit && d.status !== 'termine' && (
                        <button onClick={async () => {
                          if (!window.confirm(`Marquer le delai "${d.description}" comme termine ?`)) return;
                          try { await axios.put(`${API}/delays/${d.delay_id}`, { status: 'termine' }); fetchData(); }
                          catch (e) { window.alert(e?.response?.data?.detail || 'Erreur'); }
                        }}
                                className="text-xs text-green-700 hover:underline font-medium"
                                title="Marquer comme termine"
                                data-testid={`terminate-delay-project-${d.delay_id}`}>
                          ✓ Terminer
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              );
            })()}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="realease-card p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Phases du projet</h3>
            <div className="space-y-2">
              {Object.entries(statusLabel).map(([key, label]) => {
                const isCurrent = project.status === key;
                const isPast = Object.keys(statusLabel).indexOf(key) < Object.keys(statusLabel).indexOf(project.status);
                return (
                  <button key={key} onClick={async () => {
                    if (!canEdit || isCurrent) return;
                    try { await axios.put(`${API}/projects/${projectId}`, { status: key }); fetchData(); } catch {}
                  }} disabled={!canEdit || isCurrent} className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${isCurrent ? 'bg-[#008A8D]/10 text-[#008A8D] font-semibold' : isPast ? 'text-green-600' : 'text-gray-400'} ${canEdit && !isCurrent ? 'hover:bg-gray-100 cursor-pointer' : ''}`}>
                    <span>{isPast ? '\u2713' : isCurrent ? '\u25B6' : '\u25CB'}</span>
                    <span className="text-sm">{label}</span>
                  </button>
                );
              })}
            </div>
            {canEdit && <p className="text-xs text-gray-400 mt-3">Cliquez sur une phase pour changer le statut</p>}
          </div>

          {/* Compromis signe + relances finance */}
          <div className="realease-card p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Compromis de vente</h3>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={!!project.compromis_signed}
                disabled={!canEdit}
                onChange={async (e) => {
                  if (!canEdit) return;
                  const checked = e.target.checked;
                  await axios.put(`${API}/projects/${projectId}`, {
                    compromis_signed: checked,
                    compromis_signed_at: checked ? (project.compromis_signed_at || new Date().toISOString()) : null,
                  });
                  fetchData();
                }}
                data-testid="compromis-signed-checkbox"
              />
              <span className="text-sm">Compromis signe</span>
            </label>
            {project.compromis_signed && (
              <div className="space-y-2">
                <label className="text-xs text-gray-500 block">Date de signature</label>
                <input type="date"
                       disabled={!canEdit}
                       value={project.compromis_signed_at ? project.compromis_signed_at.split('T')[0] : ''}
                       onChange={async (e) => {
                         if (!canEdit) return;
                         const newDate = e.target.value ? `${e.target.value}T12:00:00Z` : null;
                         await axios.put(`${API}/projects/${projectId}`, { compromis_signed_at: newDate });
                         fetchData();
                       }}
                       className="realease-input text-sm"
                       data-testid="compromis-date-input" />
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  3 relances auto seront envoyees a Julie + Amelie : <b>J-31</b>, <b>J-15</b>, <b>J-7</b> avant cette date.
                </p>
              </div>
            )}
          </div>

          {/* Pense-bete du charge de projet */}
          <ProjectChecklist projectId={projectId} initial={project.checklist} />

          {/* Acte authentique + facture commission d'apport d'affaires */}
          <div className="realease-card p-6" data-testid="deed-commission-card">
            <h3 className="font-semibold text-gray-900 mb-3">Acte authentique &amp; commission d'apport</h3>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={!!project.deed_signed}
                disabled={!canEdit}
                onChange={async (e) => {
                  if (!canEdit) return;
                  const checked = e.target.checked;
                  await axios.put(`${API}/projects/${projectId}`, {
                    deed_signed: checked,
                    deed_signed_at: checked ? (project.deed_signed_at || new Date().toISOString()) : null,
                  });
                  fetchData();
                }}
                data-testid="deed-signed-checkbox"
              />
              <span className="text-sm">Acte authentique signé</span>
            </label>
            {project.deed_signed && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Date de signature de l'acte</label>
                  <input type="date"
                         disabled={!canEdit}
                         value={project.deed_signed_at ? project.deed_signed_at.split('T')[0] : ''}
                         onChange={async (e) => {
                           if (!canEdit) return;
                           const newDate = e.target.value ? `${e.target.value}T12:00:00Z` : null;
                           await axios.put(`${API}/projects/${projectId}`, { deed_signed_at: newDate });
                           fetchData();
                         }}
                         className="realease-input text-sm"
                         data-testid="deed-date-input" />
                </div>
                <CommissionInvoicesCard
                  projectId={projectId}
                  invoices={project.commission_invoices || []}
                  canEdit={canEdit}
                  onChanged={fetchData}
                />
              </div>
            )}
          </div>

          <FinanceDeadlineWidget projectId={projectId} project={project} onUpdated={fetchData} />
        </div>
      </div>

      {/* ===== Prévisionnel travaux (budget par intervenant) ===== */}
      <div className="mt-8 realease-card p-5" data-testid="works-budget-card">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Prévisionnel travaux</h2>
        <p className="text-xs text-gray-500 mb-4">Une ligne par intervenant avec ses dates d'intervention et son montant : le camembert, la barre du budget restant et le planning prévisionnel ci-dessous se mettent à jour automatiquement.</p>
        <MaitriseOeuvreCard projectId={projectId} onSaved={() => setBudgetKey(k => k + 1)} />
        <div className="mt-4">
          <WorksBudgetSection key={budgetKey} projectId={projectId} onSaved={() => setPlanningKey(k => k + 1)} />
        </div>
      </div>

      {/* ===== Planning prévisionnel (généré depuis les interventions) ===== */}
      <div className="mt-6 realease-card p-5" data-testid="works-planning-card">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Planning prévisionnel</h2>
        <p className="text-xs text-gray-500 mb-4">Généré automatiquement depuis les intervenants du prévisionnel travaux (dates modifiables ici aussi) + jalons libres.</p>
        <WorksScheduleSection key={planningKey} projectId={projectId} mode="internal" />
      </div>

      {/* Comptes rendus de réunion — juste sous le prévisionnel (demande user) */}
      <div className="mt-6" data-testid="meeting-notes-section">
        <Collapsible title="Comptes rendus de réunion & notes internes" subtitle="mentions @, partage client possible" testId="section-meetings" defaultOpen>
          <ProjectNotes projectId={projectId} />
        </Collapsible>
      </div>

      {project?.analysis_id && <ProjectScenarioDiff analysisId={project.analysis_id} />}

      {/* Sections secondaires repliees pour garder une page epuree */}
      <div className="mt-8 space-y-4">
        <Collapsible title="Analyse de rentabilité" subtitle="édition inline, synchronisée avec l'onglet Analyse projets" testId="section-analysis">
          <ProjectAnalysisInline project={project} onAnalysisCreated={() => fetchData()} />
        </Collapsible>
        {(['livraison', 'termine'].includes(project.status) || project.gl_property_id) && (
          <div className="realease-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="gl-from-project-card">
            <div>
              <p className="text-sm font-semibold text-gray-900">Gestion locative / mise en location</p>
              <p className="text-xs text-gray-500">
                {project.gl_property_id
                  ? 'La fiche du bien existe : mandats, baux et signature électronique s\'y trouvent.'
                  : 'Créez la fiche du bien pré-remplie (propriétaire, email, adresse) pour envoyer le mandat de mise en location.'}
              </p>
            </div>
            {project.gl_property_id ? (
              <a href={`/gestion/${project.gl_property_id}`}
                 className="text-sm px-4 py-1.5 rounded-lg bg-[#008A8D] hover:bg-[#006B6D] text-white font-semibold"
                 data-testid="gl-view-property-btn">Voir le bien</a>
            ) : canEdit && (
              <button
                onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/gestion/from-project/${projectId}`);
                    window.location.href = `/gestion/${r.data.property_id}`;
                  } catch (e) { alert(e.response?.data?.detail || 'Erreur lors de la création du bien'); }
                }}
                className="text-sm px-4 py-1.5 rounded-lg bg-[#008A8D] hover:bg-[#006B6D] text-white font-semibold"
                data-testid="gl-create-property-btn">Créer le bien en gestion locative</button>
            )}
          </div>
        )}
        <Collapsible title="Accès clients" subtitle="portail client & co-investisseurs" testId="section-clients">
          <CoInvestorsSection projectId={projectId} project={project} />
        </Collapsible>
        <Collapsible title="Maître d'œuvre" subtitle="accès portail MOE & comptes rendus de réunion" testId="section-moe">
          <MoeSection projectId={projectId} project={project} canEdit={canEdit} onChanged={fetchData} />
        </Collapsible>
        <Collapsible title="Entreprises travaux" subtitle="inviter un artisan sur le portail" testId="section-artisans">
          <ArtisanInvitePanel />
        </Collapsible>
        <Collapsible title="Dossier final RealEase" subtitle="livrable de fin de projet pour le client" testId="section-final-dossier">
          <FinalDossierTab projectId={projectId} projectName={project.name} projectStatus={project.status} />
        </Collapsible>
      </div>

      {showAddDelay && (
        <AddDelayModal
          projects={[{ project_id: projectId, name: project.name, client_name: project.client_name }]}
          defaultProjectId={projectId}
          onClose={() => setShowAddDelay(false)}
          onSuccess={() => { setShowAddDelay(false); fetchData(); }}
        />
      )}

      {editDelay && (
        <AddDelayModal
          projects={[{ project_id: projectId, name: project.name, client_name: project.client_name }]}
          editDelay={editDelay}
          onClose={() => setEditDelay(null)}
          onSuccess={() => { setEditDelay(null); fetchData(); }}
        />
      )}

      {showChangeClient && (
        <ChangeProjectClientModal
          project={project}
          onClose={() => setShowChangeClient(false)}
          onSuccess={() => { setShowChangeClient(false); fetchData(); }}
        />
      )}

      {showPauseModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pause-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Mettre le projet en pause</h3>
            <p className="text-sm text-gray-600 mb-4">Le projet restera visible mais marque comme en pause.</p>
            <label className="realease-label">Motif (optionnel)</label>
            <textarea
              value={pauseReason}
              onChange={e => setPauseReason(e.target.value)}
              className="realease-input h-24 resize-none"
              placeholder="Ex: En attente reponse banque, vacances client..."
              data-testid="pause-reason-input"
            />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => { setShowPauseModal(false); setPauseReason(''); }} className="px-4 py-2 border border-gray-300 rounded-lg" data-testid="pause-cancel-btn">Annuler</button>
              <button onClick={handlePauseToggle} className="px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700" data-testid="pause-confirm-btn">Mettre en pause</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== DELAYS PAGE - Dead code removed (iter31) ====================
// La page /delais est rendue par DeadlinesPage.js (importe en haut). Le filtre Branche
// y a ete integre directement. Cette section est intentionnellement vide.

// Change Project Client Modal - reassigne le client (lead) sans tout reprendre
const ChangeProjectClientModal = ({ project, onClose, onSuccess }) => {
  const [search, setSearch] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!search || search.length < 2) { setLeads([]); return; }
      setLoading(true);
      try {
        const r = await axios.get(`${API}/crm/leads?search=${encodeURIComponent(search)}`);
        // Exclut le lead actuel du projet
        setLeads((r.data || []).filter(l => l.lead_id !== project.lead_id).slice(0, 10));
      } catch { setLeads([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, project.lead_id]);

  const handleSubmit = async () => {
    if (!selected) return;
    if (!window.confirm(`Reassigner ce projet a ${selected.first_name} ${selected.last_name} (${selected.email}) ?\n\nL'ancien client (${project.client_name}) ne sera plus lie a ce projet.`)) return;
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/projects/${project.project_id}/reassign-client`, { lead_id: selected.lead_id });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="change-client-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 animate-fade-in">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold">Changer le client du projet</h2>
            <p className="text-sm text-gray-500 mt-1">Client actuel : <b>{project.client_name}</b></p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div>
            <label className="realease-label">Nouveau client (rechercher dans les leads)</label>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Tapez nom, prenom ou email (min 2 caracteres)..."
                    className="realease-input"
                    autoFocus
                    data-testid="search-new-client-input" />
          </div>
          <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
            {loading && <p className="p-4 text-center text-sm text-gray-400">Recherche...</p>}
            {!loading && leads.length === 0 && search.length >= 2 && (
              <p className="p-4 text-center text-sm text-gray-400">Aucun lead trouve.</p>
            )}
            {!loading && leads.length === 0 && search.length < 2 && (
              <p className="p-4 text-center text-sm text-gray-400">Tapez au moins 2 caracteres pour rechercher.</p>
            )}
            {leads.map(l => (
              <button key={l.lead_id} onClick={() => setSelected(l)}
                       className={`w-full text-left p-3 border-b last:border-b-0 hover:bg-gray-50 ${selected?.lead_id === l.lead_id ? 'bg-[#008A8D]/10' : ''}`}
                       data-testid={`select-lead-${l.lead_id}`}>
                <p className="font-medium text-sm">{l.first_name} {l.last_name}</p>
                <p className="text-xs text-gray-500">{l.email} {l.phone && `| ${l.phone}`}</p>
              </button>
            ))}
          </div>
          {selected && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm">
              <p className="font-semibold">Nouveau client selectionne :</p>
              <p className="text-amber-900">{selected.first_name} {selected.last_name} - {selected.email}</p>
            </div>
          )}
          <div className="flex gap-3 pt-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50">Annuler</button>
            <button onClick={handleSubmit} disabled={!selected || submitting}
                     className="flex-1 realease-btn-primary disabled:opacity-50"
                     data-testid="confirm-change-client">
              {submitting ? 'Reassignation...' : 'Reassigner'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default ProjectDetailPage;
